/**
 * Dashboard principal para entrenadores
 * 
 * Muestra:
 * - Resumen de partidos asignados
 * - Próximos partidos a evaluar
 * - Estadísticas generales
 * - Accesos rápidos a funcionalidades principales
 */

import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';

const CoachDashboard = () => {
  const { coachData: _coachData } = useOutletContext();
  const [loading] = useState(false);

  // Variable responsive
  const isMobile = window.innerWidth <= 768;

  // Scroll al top cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🏃‍♂️</div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px 16px' : '40px 24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      
      {/* Header */}
      <div style={{
        marginBottom: isMobile ? '24px' : '32px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          fontWeight: '700',
          color: '#061156',
          margin: '0 0 8px 0'
        }}>
          🏃‍♂️ Dashboard del Entrenador
        </h1>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#6c757d',
          margin: 0
        }}>
          Gestiona tus partidos y ProPoints
        </p>
      </div>

      {/* Cards de acceso rápido */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        
        {/* Mis Partidos */}
        <Link
          to="/coach/partidos"
          style={{
            display: 'block',
            padding: isMobile ? '20px' : '24px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textDecoration: 'none',
            color: 'inherit',
            border: '1px solid #e1e5e9',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '2rem',
              marginRight: '12px'
            }}>⚽</span>
            <h3 style={{
              fontSize: isMobile ? '1.1rem' : '1.3rem',
              fontWeight: '600',
              color: '#061156',
              margin: 0
            }}>
              Mis Partidos
            </h3>
          </div>
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: '#6c757d',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Ver partidos asignados y gestionar equipos
          </p>
        </Link>

        {/* ProPoints */}
        <Link
          to="/coach/evaluaciones"
          style={{
            display: 'block',
            padding: isMobile ? '20px' : '24px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textDecoration: 'none',
            color: 'inherit',
            border: '1px solid #e1e5e9',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '2rem',
              marginRight: '12px'
            }}>⭐</span>
            <h3 style={{
              fontSize: isMobile ? '1.1rem' : '1.3rem',
              fontWeight: '600',
              color: '#061156',
              margin: 0
            }}>
              ProPoints
            </h3>
          </div>
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: '#6c757d',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Gestionar ProPoints de jugadores y crear actas
          </p>
        </Link>

        {/* Mi Perfil */}
        <Link
          to="/coach/perfil"
          style={{
            display: 'block',
            padding: isMobile ? '20px' : '24px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            textDecoration: 'none',
            color: 'inherit',
            border: '1px solid #e1e5e9',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '2rem',
              marginRight: '12px'
            }}>👤</span>
            <h3 style={{
              fontSize: isMobile ? '1.1rem' : '1.3rem',
              fontWeight: '600',
              color: '#061156',
              margin: 0
            }}>
              Mi Perfil
            </h3>
          </div>
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: '#6c757d',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Ver y editar información personal
          </p>
        </Link>

      </div>

      {/* Información adicional */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '20px' : '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <h3 style={{
          fontSize: isMobile ? '1.1rem' : '1.3rem',
          fontWeight: '600',
          color: '#061156',
          margin: '0 0 16px 0'
        }}>
          📋 Próximamente
        </h3>
        <p style={{
          fontSize: isMobile ? '14px' : '15px',
          color: '#6c757d',
          margin: 0,
          lineHeight: '1.6'
        }}>
          Aquí podrás ver estadísticas detalladas, historial de ProPoints y herramientas avanzadas para el seguimiento de jugadores.
        </p>
      </div>

    </div>
  );
};

export default CoachDashboard;
